import { match } from "ts-pattern";
import { dayjs } from "../dayjs/index.js";
export const NHS_NUMBER_STATUS_TEXT = {
    "01": "Number present and verified",
    "02": "Number present but not traced",
    "03": "Trace required",
    "04": "Trace attempted - No match or multiple match found",
    "05": "Trace needs to be resolved",
    "07": "Number not present and trace not required",
};
/** How long a trace stands for before the number counts as unverified again. */
export const DEFAULT_PDS_TRACE_FREQUENCY_DAYS = 90;
const DEFAULT_TRACE_FREQUENCY = {
    value: DEFAULT_PDS_TRACE_FREQUENCY_DAYS,
    unit: "days",
};
const tracedWithin = (claim, frequency) => 
// A claim with no timestamp has never been confirmed. Said outright rather
// than left to dayjs, which reads a null as an invalid date and so answers
// false to every comparison.
claim.lastTracedAt !== null &&
    dayjs(claim.lastTracedAt).isAfter(dayjs().subtract(frequency.value, frequency.unit));
/**
 * Classify what is known about a record's NHS number.
 *
 * A supersession is something PDS reports, so it arises only where PDS has
 * spoken; where it has, it outranks everything — until someone says which of
 * the two numbers this person has, no answer about either is safe to act on.
 */
export function nhsNumberStatus(state, traceFrequency = DEFAULT_TRACE_FREQUENCY) {
    const { fromPds, fromElsewhere, supersessionUnresolved } = state;
    if (fromPds !== null) {
        if (supersessionUnresolved)
            return { code: "05" };
        if (fromPds.rejectedByPds)
            return { code: "04", source: fromPds.source };
        // A difference between the two numbers would have shown as a supersession
        // above, so by here they are the same number: whichever was confirmed
        // later says when it was last known good.
        const confirmedRecently = tracedWithin(fromPds, traceFrequency) ||
            (fromElsewhere !== null && tracedWithin(fromElsewhere, traceFrequency));
        return confirmedRecently
            ? { code: "01", source: fromPds.source }
            : { code: "03", source: fromPds.source };
    }
    if (fromElsewhere !== null) {
        return fromElsewhere.rejectedByPds
            ? { code: "04", source: fromElsewhere.source }
            : { code: "02", source: fromElsewhere.source };
    }
    return { code: "07" };
}
/**
 * Whether putting this record to PDS could tell us anything we do not know.
 *
 * A number confirmed recently, one PDS rejected, an unresolved supersession
 * and a record with no number are all settled: each needs time or a person,
 * not another trace.
 *
 * Narrows, because a caller that decides to trace then needs the number to
 * trace with, and both of these carry one.
 */
export function traceIsNeeded(code) {
    return match(code)
        .with("02", "03", () => true)
        .with("01", "04", "05", "07", () => false)
        .exhaustive();
}
//# sourceMappingURL=nhsNumberStatus.js.map